import React, { useEffect, useState, useCallback } from 'react';
import platformMajAnimationLogoDto from '../template/homeGhajari3TopTemplate';
import { Box, Typography } from '@mui/material';

export interface PlatformMajAnimationLogoProps {
    scrollY: number;
    windowSize: {
        width: number;
        height: number;
    };
    item: platformMajAnimationLogoDto;
}

const PlatformMajAnimationLogo: React.FC<PlatformMajAnimationLogoProps> = (props) => {
    const { scrollY, windowSize, item } = props;

    const [picLogo, setPicLogo] = useState(0);
    const [position, setPosition] = useState<'fixed' | 'relative'>('fixed');
    const [justifyContent, setJustifyContent] = useState<'flex-start' | 'flex-end'>('flex-start');
    const [height] = useState(item.length * item.distance - item.distance);
    const [descriptions, setDescriptions] = useState(item.descriptions);

    const getElementId = () => `pmAnimationLogo${item.id}`;
    const startItem = () => document.getElementById(getElementId())?.offsetTop ?? 0;
    const scrollYInItem = () => scrollY - startItem();
    const endItem = () => startItem() + height;
    const itemScrollHeight = () => height + windowSize.height;
    const lastLogoItem = () => Math.floor(item.length - 1);
    const setLogoItem = () => Math.floor(scrollYInItem() / item.distance);
    const hasDescription = () => item.withDescription;
    const descriptionType = () => item.descriptionType ?? 1;
    const lastDescriptionItem = () => descriptions.length - 1;

    const setDescriptionItem = () =>
        descriptions.findIndex(row => row.start + startItem() > scrollY) !== -1
            ? descriptions.findIndex(row => row.start + startItem() > scrollY)
            : lastDescriptionItem();

    const resetDescriptionType1 = (input: boolean) => {
        setDescriptions(descriptions.map(row => ({ ...row, onScroll: input })));
    };

    const handleDescriptionType1 = (index: number) => {
        if (index >= 0 && index <= lastDescriptionItem()) {
            const updated = [...descriptions];
            updated[index].onScroll = true;
            setDescriptions(updated);
        }
    };

    const shouldShowDescriptionAt = (start: number) => scrollYInItem() >= start;

    const renderImages = useCallback(() => {
        return (
            <Box display='flex' justifyContent='center' alignItems='center' sx={{ minWidth: { xs: '300px', md: '400px' }, maxWidth: { xs: '300px', md: '500px' } }}>
                {Array.from({ length: item.length }).map((_, i) => (
                    <img
                        key={i}
                        src={`${process.env.PUBLIC_URL}/${item.id}/${i}${item.fileFormat}`}
                        alt={`Logo frame ${i}`}
                        style={{
                            display: picLogo !== i ? 'none' : 'inline-block',
                            width: '70%',
                            height: 'auto',
                        }}
                    />
                ))}
            </Box>
        );
    }, [picLogo, item]);

    const updateLogo = useCallback(() => {
        const start = startItem();
        const end = endItem();

        if (scrollY < start) {
            setPicLogo(0);
            setPosition('relative');
            setJustifyContent('flex-start');
        } else if (scrollY > end) {
            setPicLogo(lastLogoItem());
            setPosition('relative');
            setJustifyContent('flex-end');
        } else {
            setPicLogo(setLogoItem());
            setPosition('fixed');
            setJustifyContent('flex-start');
        }
    }, [scrollY, windowSize]);

    const updateDescription = useCallback(() => {
        const start = startItem();
        const end = endItem();

        if (hasDescription()) {
            switch (descriptionType()) {
                case 1:
                    if (scrollY < start) {
                        resetDescriptionType1(false);
                    } else if (scrollY > end) {
                        resetDescriptionType1(true);
                    } else {
                        handleDescriptionType1(setDescriptionItem());
                    }
                    break;
            }
        }
    }, [scrollY, windowSize]);

    useEffect(() => {
        updateLogo();
        updateDescription();
    }, [scrollY, windowSize, updateLogo, updateDescription]);

    return (
        <Box

            component="section"
            display="flex"
            flexDirection='column'
            justifyContent={justifyContent}
            sx={{
                height: itemScrollHeight(),
                minHeight: itemScrollHeight(),

            }}
            id={getElementId()}
        >
            <Box
                display="flex"
                flexDirection={{ xs: "column", md: "row" }}
                justifyContent={{ xs: "center", md: "space-evenly" }}
                alignItems='center'
                gap={4}
                style={{ top: 0, position, width: '100vw', height: '100vh' }}
                sx={{
                    zIndex: '2',
                    //backgroundColor: '#dde3e1',
                    backgroundColor: '#c4d0ce',
                }}
            >
                {renderImages()}

                {hasDescription() && (
                    <Box sx={{ minWidth: { xs: '300px', md: '400px' }, maxWidth: { xs: '300px', md: '500px' } }} >
                        <Typography
                            variant='h6' fontWeight="bold"
                            sx={{
                                transition: '1s linear',
                                opacity: shouldShowDescriptionAt(0) ? 1 : 0,
                                transform: shouldShowDescriptionAt(0) ? 'translateY(-50px)' : 'none'
                            }}
                        >Skills</Typography>
                        <Typography
                            variant='body2' mt={1.5}
                            sx={{
                                transition: '1s linear',
                                opacity: shouldShowDescriptionAt(150) ? 1 : 0,
                                transform: shouldShowDescriptionAt(150) ? 'translateY(-50px)' : 'none'
                            }}
                        ><b>Front-end Development: </b>React JS, Redux, Material-UI, HTML, CSS, Sass, JavaScript (ES5/ES6), TypeScript, Single-Page Applications , Responsive Web Design</Typography>
                        <Typography
                            variant='body2' mt={1.5}
                            sx={{
                                transition: '1s linear',
                                opacity: shouldShowDescriptionAt(300) ? 1 : 0,
                                transform: shouldShowDescriptionAt(300) ? 'translateY(-50px)' : 'none'
                            }}
                        ><b>Back-end Development: </b>ASP.NET Core, C#, RESTful APIs, Domain-Driven Design, Object-Oriented Programming, Design Patterns</Typography>
                        <Typography
                            variant='body2' mt={1.5}
                            sx={{
                                transition: '1s linear',
                                opacity: shouldShowDescriptionAt(450) ? 1 : 0,
                                transform: shouldShowDescriptionAt(450) ? 'translateY(-50px)' : 'none'
                            }}
                        ><b>Databases: </b>Microsoft SQL Server, Elasticsearch (NoSQL)</Typography>
                        <Typography
                            variant='body2' mt={1.5}
                            sx={{
                                transition: '1s linear',
                                opacity: shouldShowDescriptionAt(600) ? 1 : 0,
                                transform: shouldShowDescriptionAt(600) ? 'translateY(-50px)' : 'none'
                            }}
                        ><b>Tools & Platforms: </b>Git, Visual Studio, Azure, Agile/Scrum, AWS (S3, CloudFront, Route 53, Certificate Manager)</Typography>
                        <Typography
                            variant='body2' mt={1.5}
                            sx={{
                                transition: '1s linear',
                                opacity: shouldShowDescriptionAt(750) ? 1 : 0,
                                transform: shouldShowDescriptionAt(750) ? 'translateY(-50px)' : 'none'
                            }}
                        ><b>Other Skills: </b>T-SQL, Unit Testing, CI/CD Pipelines</Typography>
                        <Typography
                            variant='body2' mt={1.5}
                            sx={{
                                transition: '1s linear',
                                opacity: shouldShowDescriptionAt(900) ? 1 : 0,
                                transform: shouldShowDescriptionAt(900) ? 'translateY(-50px)' : 'none'
                            }}
                        ><b>Soft Skills: </b>Problem-solving, teamwork, adaptability, communication, time management, attention to detail, and critical thinking</Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default PlatformMajAnimationLogo;